import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch, HiPencil, HiTrash } from 'react-icons/hi';
import { Button, Input, Select, Card, CardHeader, Table, Badge, Pagination, Modal } from '../components/ui';
import { listUsers, createUser, updateUser, deleteUser } from '../services/users.api';
import { listPartners } from '../services/partners.api';
import { listOwners } from '../services/owners.api';
import { listCustomers } from '../services/customers.api';
import { formatDate } from '../utils/helpers';
import { ROLE_OPTIONS } from '../utils/constants';
import { getErrorMessage } from '../services/api';
import type { User, Role } from '../types';

export function Users() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<Role>('CUSTOMER');
  const [partnerId, setPartnerId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [ownerId, setOwnerId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['users', { page, search, role: roleFilter }],
    queryFn: () => listUsers({ page, search, role: roleFilter || undefined }),
  });

  // Linking only happens in the Edit dialog; on create you just pick the role and the
  // link is established later from the Partner/Owner/Customer create dialog.
  const { data: partnersData } = useQuery({
    queryKey: ['partners', 'dropdown'],
    queryFn: () => listPartners({ limit: 100 }),
    enabled: !!editingUser && role === 'PARTNER',
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers', 'dropdown'],
    queryFn: () => listCustomers({ limit: 100 }),
    enabled: !!editingUser && role === 'CUSTOMER',
  });

  const { data: ownersData } = useQuery({
    queryKey: ['owners', 'dropdown'],
    queryFn: () => listOwners({ limit: 100 }),
    enabled: !!editingUser && role === 'OWNER',
  });

  const createMutation = useMutation({
    mutationFn: () => createUser({
      email,
      password,
      firstName,
      lastName,
      role,
      partnerId: role === 'PARTNER' && partnerId ? partnerId : undefined,
      customerId: role === 'CUSTOMER' && customerId ? customerId : undefined,
      ownerId: role === 'OWNER' && ownerId ? ownerId : undefined,
    }),
    onSuccess: () => {
      toast.success('User created successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateUser(editingUser!.id, {
      email,
      firstName,
      lastName,
      role,
      partnerId: role === 'PARTNER' ? (partnerId || null) : null,
      customerId: role === 'CUSTOMER' ? (customerId || null) : null,
      ownerId: role === 'OWNER' ? (ownerId || null) : null,
    }),
    onSuccess: () => {
      toast.success('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      toast.success('User deactivated');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const openCreateModal = () => {
    resetForm();
    setEditingUser(null);
    setIsCreateOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEmail(user.email);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setRole(user.role);
    setPartnerId(user.partnerId || '');
    setCustomerId(user.customerId || '');
    setOwnerId(user.ownerId || '');
    setPassword('');
    setIsCreateOpen(true);
  };

  const closeModal = () => {
    setIsCreateOpen(false);
    setEditingUser(null);
    resetForm();
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setRole('CUSTOMER');
    setPartnerId('');
    setCustomerId('');
    setOwnerId('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (u: User) => `${u.firstName} ${u.lastName}`,
    },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (u: User) => (
        <Badge status={u.role === 'ADMIN' ? 'ACTIVE' : 'PENDING'}>
          {ROLE_OPTIONS.find((r) => r.value === u.role)?.label || u.role}
        </Badge>
      ),
    },
    {
      key: 'linkedTo',
      header: 'Linked To',
      render: (u: User) => {
        if (u.partner) return u.partner.name;
        if (u.customer) return u.customer.name;
        return '-';
      },
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (u: User) => (
        <Badge status={u.isActive ? 'ACTIVE' : 'EXPIRED'}>
          {u.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (u: User) => formatDate(u.createdAt!),
    },
    {
      key: 'actions',
      header: '',
      render: (u: User) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => openEditModal(u)}>
            <HiPencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm('Deactivate this user?')) {
                deleteMutation.mutate(u.id);
              }
            }}
          >
            <HiTrash className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <CardHeader
        title="Users"
        description="Manage user accounts"
        action={
          <Button onClick={openCreateModal}>
            <HiPlus className="w-4 h-4 mr-2" />
            New User
          </Button>
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              options={[{ value: '', label: 'All Roles' }, ...ROLE_OPTIONS]}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as Role | '')}
              className="w-48"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(user) => user.id}
          isLoading={isLoading}
          emptyMessage="No users found"
        />

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Modal
        isOpen={isCreateOpen}
        onClose={closeModal}
        title={editingUser ? 'Edit User' : 'Create New User'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <Input
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {!editingUser && (
            <Input
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          )}
          <Select
            label="Role"
            options={[...ROLE_OPTIONS]}
            value={role}
            onChange={(e) => {
              setRole(e.target.value as Role);
              setPartnerId('');
              setCustomerId('');
              setOwnerId('');
            }}
            required
          />
          {editingUser && role === 'OWNER' && (
            <Select
              label="Link to Owner"
              options={[
                { value: '', label: 'Select an owner...' },
                ...(ownersData?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
              ]}
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            />
          )}
          {editingUser && role === 'PARTNER' && (
            <Select
              label="Link to Partner"
              options={[
                { value: '', label: 'Select a partner...' },
                ...(partnersData?.data?.map((p) => ({ value: p.id, label: p.name })) || []),
              ]}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            />
          )}
          {editingUser && role === 'CUSTOMER' && (
            <Select
              label="Link to Customer"
              options={[
                { value: '', label: 'Select a customer...' },
                ...(customersData?.data?.map((c) => ({ value: c.id, label: `${c.name}${c.company ? ` (${c.company})` : ''}` })) || []),
              ]}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            />
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
